import { BROWSER, DEV } from 'esm-env';
import {
  atom,
  type WritableAtom,
  type ReadableAtom,
  computed } from 'nanostores';
import type { ManageAPI } from '~/manage-api/types';
import { type ReverseRoute, ReverseRouter } from '~/reverse-router';
import { scopedValueGetters, valueGetters } from './components/field-utils';
import { uniq } from 'lodash-es';
import { L10nService, type Translator } from './service/l10n';
import { type Alternate } from './metadata';
import { viaCDN } from './util/url';


export type StoresKey = {
  id: string;
}

export type Theme = {
  name: string;
  key: string;
  key_subfix?: string;
};

export type Site = {
  name: string;
  cdn_host: string;
  default_locale: string;
  recaptcha_site_key?: string;
  restaurantLineOfBusinessIDs?: string;
  giftCardDisabled?:boolean;
  offersLanguage?:string;
  theme: Theme;
  logo?: string;
  shoppingCenterName?: string;
  supportedLanguages?: string[];
  openingHoursText?: string;
  shoppingCenterAddress?: string;
  contactEmail?: string;
  contactNumber?: string;
}

export interface BreadCrumb  {
  title: string, href: string
}

export interface StoresContextData {
  languages: Language[];
  locale: string;
  nav_menus: NavigationMenu[];
  site: Site;
  site_page: SitePage;
  page_master: SitePage;
  site_pages: ThinPage[];
  site_resources:SiteResource[];
  alternate: Alternate[];
  site_groupings: SiteGroupings | undefined;

  banners: ManageAPI.Banner[];
  event: ManageAPI.Event;
  events: ManageAPI.Event[];
  floors: ManageAPI.FloorBase[];
  offers: ManageAPI.Offer[];
  offers_ext: ManageAPI.OffersExt;
  offer_picks: ManageAPI.Offer[];
  scdata: ManageAPI.ShoppingCenterData;
  stores: ManageAPI.Store[];
  store: ManageAPI.Store;
  instagramFeeds: Instagram[];
  lunch: ManageAPI.Lunch[];
  opening_hours: ManageAPI.OpeningHours;
  search_results: ManageAPI.SearchResults;
  search_input : string;
  reverse_router: ReverseRoute[];
  breadcrumbs: BreadCrumb[];
  service_locations: ManageAPI.ServiceLocation[];
  digital_signage_data: ManageAPI.DigitalSignageData | null;
}

// Type of entry after Object.entries() is used
type StoresContextDataEntry = [keyof StoresContextData, StoresContextData[keyof StoresContextData]];
type StoresContextEntry = [keyof StoresContext, StoresContext[keyof StoresContext]];

type StoresContextKey = keyof StoresContext;


// Override Object interface for custom Object.entries():
interface StoresContext_Object extends ObjectConstructor {
  entries<K extends keyof StoresContextData, T extends StoresContextData[K]>(
    o: { [s in K]: T } 
  ): [K, T][];

  keys<K extends StoresContextKey>(o: { [s in K]: any }): K[];
}

// Let's use this interface now:
const Obj: StoresContext_Object = Object;

type StoresContextInterface = {
  readonly [Property in keyof Omit<StoresContextData, 'reverse_router'>]: WritableAtom<StoresContextData[Property]>;
};

export type I18nOf = <T>(value_i18n: Obj_i18n<T>, default_value?: T) => T | undefined;
export type ValueOf = (value: string | Value<string> | Value_i18n, _default?: string) => string;

const empty_router = new ReverseRouter([], atom('en'));

export class StoresContext implements StoresContextInterface {

  readonly languages: WritableAtom<Language[]>;
  readonly locale: WritableAtom<string>;
  readonly lang: ReadableAtom<string>;
  readonly nav_menus: WritableAtom<NavigationMenu[]>;
  readonly site: WritableAtom<Site>;
  readonly site_page: WritableAtom<SitePage>;
  readonly page_master: WritableAtom<SitePage>;
  readonly site_pages: WritableAtom<ThinPage[]>;
  readonly site_details: WritableAtom<SiteDetails>
  readonly site_resources: WritableAtom<SiteResource[]>
  readonly alternate: WritableAtom<Alternate[]>
  readonly site_groupings: WritableAtom<SiteGroupings | undefined>;

  readonly banners: WritableAtom<ManageAPI.Banner[]>;
  readonly event: WritableAtom<ManageAPI.Event>;
  readonly events: WritableAtom<ManageAPI.Event[]>;
  readonly floors: WritableAtom<ManageAPI.FloorBase[]>;
  readonly offers: WritableAtom<ManageAPI.Offer[]>;
  readonly offers_ext: WritableAtom<ManageAPI.OffersExt>;
  readonly offer_picks: WritableAtom<ManageAPI.Offer[]>;
  readonly scdata: WritableAtom<ManageAPI.ShoppingCenterData>;
  readonly stores: WritableAtom<ManageAPI.Store[]>;
  readonly store: WritableAtom<ManageAPI.Store>;
  readonly instagramFeeds: WritableAtom<Instagram[]>;
  readonly lunch: WritableAtom<ManageAPI.Lunch[]>;
  readonly opening_hours: WritableAtom<ManageAPI.OpeningHours>;
  readonly search_results: WritableAtom<ManageAPI.SearchResults>;
  readonly search_input: WritableAtom<string>;
  readonly breadcrumbs: WritableAtom<BreadCrumb[]>;
  readonly service_locations: WritableAtom<ManageAPI.ServiceLocation[]>;
  readonly digital_signage_data: WritableAtom<ManageAPI.DigitalSignageData | null>;

  readonly stylebook: WritableAtom<Stylebook>;

  reverse_router: WritableAtom<ReverseRouter>;
  readonly __: ReadableAtom;
  readonly T: ReadableAtom<Translator>;

  i18nOf: I18nOf;
  valueOf: ValueOf;
  viaCDN: (url: string) => string;

  private ssr_only = [
    '__', 'T', 'lang', 
    'site_pages', 'site_details', 'site_resources',
    'page_master', 'stylebook'
  ];

  private constructor () {
    this.languages = atom([]);
    this.locale = atom('');
    this.lang = computed(this.locale, (locale) => locale.slice(0,2))
    this.nav_menus = atom([]);
    this.site = atom<Site>({
      name: '',
      cdn_host: '',
      default_locale: '',
      theme: { key: '', name: '' }
    });
    this.site_page = atom(null);
    this.page_master = atom(null);
    this.site_pages = atom([]);
    this.site_details = atom(null);
    this.site_resources = atom([]);
    this.alternate = atom([]);
    this.site_groupings = atom();

    this.banners = atom([]);
    this.event = atom(null);
    this.events = atom([]);
    this.floors = atom([]);
    this.offers = atom([]);
    this.offers_ext = atom([]);
    this.offer_picks = atom([]);
    this.scdata = atom(null);
    this.stores = atom([]);
    this.lunch = atom([]);
    this.opening_hours = atom(null)
    this.search_results = atom(null)
    this.search_input = atom("")
    this.store = atom(null);
    this.instagramFeeds = atom([]);
    this.breadcrumbs = atom([])
    this.service_locations = atom([]);
    this.digital_signage_data = atom(null);
    this.stylebook = atom(null);

    this.reverse_router = atom(empty_router);
    const locales = computed(
      this.locale, locale => uniq([locale, this.site.get().default_locale, 'en-US'])
    );
    this.__ = computed(locales, (locales) => localizer(locales));

    this.T = computed(
      [this.locale, this.site], 
      (locale, site) => L10nService.getTranslator(site?.theme.key, locale)
    );

    function localizer (locales: string[]) {
      return <T>(value_i18n: Obj_i18n<T> = {}, default_value?: T): T | undefined => {
        return i18nValue(locales, value_i18n, default_value);
      }
    }

    function i18nValue <T>(locales: string[], value_i18n: Obj_i18n<T> = {}, default_value?: T): T | undefined {
      let value;
      // Fun fact: sometimes Liferay locales are with dash and in another API w/ underscore
      for (const locale of locales) {
        if (!locale) continue;
        const locale_mod = locale.charAt(2) == '-' ? locale.replace('-', '_') : locale.replace('_', '-')
        value = locale && (value_i18n[locale] || value_i18n[locale_mod]);
        if (value != undefined) {
          break;
        }
      }
      return value || default_value;
    }

    this.i18nOf = <T>(value_i18n: Obj_i18n<T> = {}, default_value?: T): T | undefined => {
      return i18nValue(locales.get(), value_i18n, default_value);
    }

    this.valueOf = (value: string | Value<string> | Value_i18n, _default = ''): string => {      
      if (value == undefined) return _default;
      else if (typeof value != 'object') return value;
      else if ('value' in value) return value.value;
      else if ('value_i18n' in value) return this.i18nOf(value.value_i18n, _default) || _default;
      return _default;
    }

    this.viaCDN = (url: string) => {
      return viaCDN(url, this.site.get());
    }

  }

  valueOfURL (value: string | Value<string> | Value_i18n) {
    const val = this.valueOf(value);
    return val ? this.viaCDN(val) : val;
  }

  fieldUtils () {
    return valueGetters(this);
  }

  /**
   * Tidiest way to use field utilities in a component
   * @param fragment - The fragment the field utilities will be scoped to
   * @returns 
   */
  scopedUtils (fragment: Fragment) {
    return scopedValueGetters(this, fragment);
  }

  toJSON () {
    const obj = Obj.keys(this).reduce((acc, key: StoresContextKey) => {

      if (this.ssr_only.includes(key)) return acc;

      const prop = this[key]

      if ('get' in prop && typeof prop.get == 'function') {
        acc[key] = prop.get();
      }
      
      return acc;
    }, {} as Record<StoresContextKey, any>);
    
    obj.reverse_router = this.reverse_router.get().toJSON();

    return obj;
  }

  static from (data: StoresContextData, request_store_key: StoresKey): StoresContext {
    if (!BROWSER) throw new Error("Not for use during SSR!");
    if (DEV) console.info('Init StoresContext from page data');
    
    const context = getStoresContext(request_store_key);

    for (const [key, value] of Obj.entries(data)) {
      if (key == 'reverse_router') {
        continue;
      }
      // @ts-ignore - haven't figured out how to make value match
      context[key].set(value);
    }
    context.reverse_router.set(
      new ReverseRouter(data.reverse_router, context.locale)
    );

    return context;
  }

  static getInstance () {
    return new StoresContext();
  }
  
}

const stores_map = new WeakMap();

function getStoresContext_SSR (request_store_key: StoresKey): StoresContext {

  if (!stores_map.has(request_store_key)) {
    try {
      stores_map.set(request_store_key, StoresContext.getInstance());
    } catch (error) {
      console.error(`Failed inserting`, request_store_key);
      console.error(error);
    }
  }

  return (stores_map.get(request_store_key) || {}) as StoresContext;
}

const getStoresContext_Browser = (
  (single_sc: StoresContext) => (_request_store_key: StoresKey): StoresContext => single_sc
)(StoresContext.getInstance());

export const getStoresContext = BROWSER ? getStoresContext_Browser : getStoresContext_SSR;
